import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { motion } from 'framer-motion';
import './FeedDonationForm.css';

const FeedDonationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    amount: '',
    amountCustom: '',
    plan: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const amounts = ['₹20', '₹50', '₹100', 'Custom'];
  const plans = ['One-time', 'Daily', 'Monthly', 'Yearly'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear custom amount if not "Custom"
      ...(name === 'amount' && value !== 'Custom' ? { amountCustom: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Prepare amount value
      let finalAmount = formData.amount;
      if (formData.amount === 'Custom' && formData.amountCustom) {
        finalAmount = `₹${formData.amountCustom}`;
      }

      // Validate amount
      if (!finalAmount || finalAmount === 'Custom') {
        throw new Error('Please select or enter an amount');
      }

      // Save to Firestore collection: feed_donations
      const docRef = await addDoc(collection(db, 'feed_donations'), {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim().toLowerCase() || '',
        city: formData.city.trim(),
        amount: finalAmount,
        plan: formData.plan,
        message: formData.message.trim() || '',
        status: 'pending',
        created_at: serverTimestamp(),
        source: 'feed_the_cows_page'
      });

      console.log('Feed donation saved with ID:', docRef.id);

      // Trigger notification hook
      try {
        await addDoc(collection(db, 'notifications'), {
          type: 'feed_donation',
          title: 'New Feed Donation',
          message: `${formData.name} from ${formData.city} donated ${finalAmount} for ${formData.plan} plan`,
          data: {
            donationId: docRef.id,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            city: formData.city,
            amount: finalAmount,
            plan: formData.plan
          },
          status: 'unread',
          created_at: serverTimestamp(),
          target_roles: ['admin', 'adminassistant', 'operationhead']
        });
      } catch (notifError) {
        console.warn('Notification creation failed (non-critical):', notifError);
      }

      // Clear form and show success
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        amount: '',
        amountCustom: '',
        plan: '',
        message: ''
      });
      setSuccess(true);
      
      // Scroll to top of form to show success message
      setTimeout(() => {
        document.getElementById('donation-form')?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);

    } catch (err) {
      console.error('Error submitting feed donation:', err);
      let errorMessage = 'Failed to submit. Please try again or contact us directly.';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please ensure you have the correct permissions.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="feed-donation-form" className="feed-donation-form-container">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="feed-donation-card"
      >
        <div className="form-header">
          <h2>Start Your Digital Gau-Seva</h2>
          <p className="form-subtitle">
            Fill in your details below. Your contribution will directly feed cows with real, nutritious fodder.
          </p>
        </div>

        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="success-message"
          >
            <div className="success-icon">🙏</div>
            <h3>Thank you! Your seva will feed real cows today.</h3>
            <p>We'll process your donation and send you a confirmation email shortly. Your contribution makes a real difference.</p>
          </motion.div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="feed-donation-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="10-digit mobile number"
                  pattern="[0-9]{10}"
                  maxLength="10"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address (Optional)</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="gold-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="gold-input"
                  placeholder="Your city"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="amount">Donation Amount *</label>
                <select
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  className="gold-input"
                >
                  <option value="">Select amount</option>
                  {amounts.map(amount => (
                    <option key={amount} value={amount}>{amount}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="plan">Seva Plan *</label>
                <select
                  id="plan"
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  required
                  className="gold-input"
                >
                  <option value="">Select plan</option>
                  {plans.map(plan => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.amount === 'Custom' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="amountCustom">Enter Custom Amount (₹) *</label>
                  <input
                    type="number"
                    id="amountCustom"
                    name="amountCustom"
                    value={formData.amountCustom}
                    onChange={handleChange}
                    required
                    min="20"
                    className="gold-input"
                    placeholder="Minimum ₹20"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="message">Message or Intention (Optional)</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="gold-input"
                placeholder="Any special message, prayer, or intention for this seva..."
                rows="4"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-submit"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Seva'}
            </button>

            <p className="form-footer">
              By submitting, you agree to our terms. Your donation will be used to purchase and distribute real fodder to cows in our managed farm system.
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default FeedDonationForm;

